/**
 * Trip Analysis Service
 * Hämtar bin-fil-analys (vad bin-filen skulle ge) för admin-modulen "Analysera Trippar".
 */

import api from '@/services/api/client';

/** En tripp som bin-filen skulle ge. */
export interface BinTrip {
  start: string;
  end: string;
  distanceM: number;
  points: number;
  hasAvl250: boolean;
  maxSpeedKmh: number;
  reconstructed?: boolean;
  distanceSource?: 'gps' | 'oem' | 'osrm' | 'straight';
}

/** Aggregerad rådata-statistik för bin-punkter. */
export interface BinRawStats {
  records: number;
  speedMaxKmh: number;
  pctSpeedGt0: number;
  medianIntervalSec: number;
  gapsGt120: number;
  avl250Count: number;
  satZeroPct: number;
  firstTs: string | null;
  lastTs: string | null;
}

export interface BinAnalysisResult {
  imei: string;
  fromDate: string;
  toDate: string;
  gpsDataDirExists: boolean;
  keptFileCount: number;
  totalGpsPoints: number;
  pointsWithAvl250: number;
  firstPointTs: string | null;
  lastPointTs: string | null;
  deviceMapping: { vehicleId: string; organizationId?: string; driverId?: string };
  tripsWouldSaveAfterMinDurationDistance: number;
  tripsSkippedShort: number;
  candidateDecisions: Array<{ path: string; fileImei: string; dayUtc: string; kept: boolean; reason: string }>;
  allTrips?: BinTrip[];
  rawStats?: BinRawStats;
  rawStatsByDay?: Array<{ day: string } & BinRawStats>;
  error?: string;
}

interface BinAnalysisApiResponse {
  success: boolean;
  data?: BinAnalysisResult;
  message?: string;
}

interface ReplayApiResponse {
  success: boolean;
  data?: { id?: string; status?: string };
  message?: string;
}

class TripAnalysisService {
  /** Hämta vad fordonets bin-fil skulle ge för trippar i datumspannet (read-only). */
  async getBinAnalysis(params: { vehicleId: string; from: string; to: string }): Promise<BinAnalysisResult> {
    const response = await api.get<BinAnalysisApiResponse>('/api/trip-analysis/bin', {
      params: { vehicleId: params.vehicleId, from: params.from, to: params.to },
    });
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Kunde inte analysera bin-fil');
  }

  /** Fas B: kör replay/reparation från bin-fil (kräver REPLAY-behörighet). */
  async runReplay(params: { vehicleId: string; from: string; to: string }): Promise<{ id?: string; status?: string }> {
    const response = await api.post<ReplayApiResponse>('/api/trip-analysis/replay', params);
    if (response.data.success) {
      return response.data.data || {};
    }
    throw new Error(response.data.message || 'Kunde inte starta replay');
  }
}

export const tripAnalysisService = new TripAnalysisService();
export default tripAnalysisService;
